import _ from 'lodash';
import { Field, useFormState } from 'react-final-form';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { InputGroup } from '@seeqdev/qomponents';
import type React from 'react';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export interface ValueWithSymbolIF extends ValidatingFormComponent<number> {
  component: 'ValueWithSymbolFormComponent';
  symbol: string | React.JSX.Element;
  label?: string;
  id?: string;
  min?: number;
  max?: number;
}

/**
 * Text input field (form component) with symbol/unit
 */
export const ValueWithSymbolFormComponent = (props: ValueWithSymbolIF) => {
  const {
    name,
    testId = 'valueWithSymbol',
    label,
    symbol,
    onChange,
    validation,
    extendValidation,
    customErrorText,
    min,
    max,
  } = props;

  const defaultValidation = (value?: number) => {
    if (!value) return true;

    const hasMin = _.isNumber(min),
      hasMax = _.isNumber(max);
    const numberValue = Number(value);
    if (hasMin || hasMax) {
      return (
        !_.isNumber(numberValue) ||
        _.isNaN(numberValue) ||
        (hasMin && numberValue < min) ||
        (hasMax && numberValue > max)
      );
    }

    return false;
  };

  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);
  const formState = useFormState();
  const modified = Object.values(formState.modified ?? {}).some(Boolean);

  const showError =
    _.has(formState.errors, name) && formState.hasValidationErrors && (modified || formState.submitFailed);

  return (
    <FormFieldWrapper
      testId={testId}
      label={label}
      showError={showError}
      customErrorText={customErrorText}
      wrapperClassNames="flexBaseline">
      <Field<number> name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const {
            onChange: formOnChange,
            value: fieldValue,
            name: fieldName,
          } = getFormFieldProps(formState, input, meta, props);
          return (
            <InputGroup
              name={fieldName}
              value={fieldValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => formOnChange(e.target.value)}
              max={max}
              min={min}
              testId={`${testId}Input`}
              showError={showError}
              extraClassNames="min-width-80"
              append={[
                {
                  variant: 'element',
                  element: (
                    <div data-testid={`${testId}Symbol`} className="tw:py-1 tw:px-2.5 input-md">
                      {symbol}
                    </div>
                  ),
                },
              ]}
            />
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
